<template>
    <div class="form enter-delivery-address-form">
        <TextInput
            class="street-input delivery-address-input"
            ref="streetComponent"
            :maxCharLength="100"
            :requirement-type="InputRequirementType.Required"
            v-model:value="street"
            :label="translations.deliveryAddress.street"
        />

        <TextInput
            class="address-details-input delivery-address-input"
            ref="addressDetailsComponent"
            :maxCharLength="200"
            :requirement-type="InputRequirementType.Optional"
            v-model:value="addressDetails"
            :label="translations.deliveryAddress.addressDetails"
        />

        <TextInput
            class="postal-code-input delivery-address-input"
            ref="postalCodeComponent"
            :maxCharLength="10"
            :requirement-type="InputRequirementType.Required"
            v-model:value="postalCode"
            :label="translations.deliveryAddress.postalCode"
        />

        <TextInput
            class="city-input delivery-address-input"
            ref="cityComponent"
            :maxCharLength="100"
            :requirement-type="InputRequirementType.Required"
            v-model:value="city"
            :label="translations.deliveryAddress.city"
        />

        <CountryDropdown
            :disabled="false"
            :model-value="country"
            inputId="delivery-address-country"
            style="margin-top: 8px; margin-bottom: 14px"
        />

        <VButton :class="{ loading: isLoading }" class="save-btn primary rounded" @click="save">{{
            trans.general.save
        }}</VButton>
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { InputRequirementType } from "@/components/elements/InputRequirementType";
    import { Factory } from "@/data/dao/Factory";
    import { DaoError } from "@/data/dao/service/BaseServiceDao";
    import { CustomerDao } from "@/data/dao/service/CustomerDao";
    import { Country } from "@/data/model/Enums";
    import { useCartStore } from "@/data/stores/cart";
    import { exceptionTypes } from "@/data/util/Config";
    import { trans } from "@/operator/localization/Trans";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { ref } from "vue";

    import TextInput from "@/components/elements/TextInput.vue";
    import VButton from "@/components/elements/VButton.vue";
    import CountryDropdown from "@/components/pay/CountryDropdown.vue";

    const cart = useCartStore(); // for getting current order service

    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

    // define props
    const props = defineProps<{
        hide?: () => void;
        isInOperatorApp: boolean;
    }>();

    const isLoading = ref<boolean>(false);
    const firstNameComponent = ref<InstanceType<typeof TextInput> | undefined>();
    const lastNameComponent = ref<InstanceType<typeof TextInput> | undefined>();
    const streetComponent = ref<InstanceType<typeof TextInput> | undefined>();
    const addressDetailsComponent = ref<InstanceType<typeof TextInput> | undefined>();
    const cityComponent = ref<InstanceType<typeof TextInput> | undefined>();
    const postalCodeComponent = ref<InstanceType<typeof TextInput> | undefined>();

    const addressDetails = ref<string | undefined>(cart.currentCart?.deliveryAddress?.addressDetails);
    const city = ref<string | undefined>(cart.currentCart?.deliveryAddress?.city);
    const postalCode = ref<string | undefined>(cart.currentCart?.deliveryAddress?.postalCode);
    const street = ref<string | undefined>(cart.currentCart?.deliveryAddress?.street);
    const country = ref<Country>(cart.currentCart?.deliveryAddress?.country ?? Country.Switzerland);

    async function save() {
        // Reset component states
        firstNameComponent.value?.setError(false);
        lastNameComponent.value?.setError(false);
        streetComponent.value?.setError(false);
        cityComponent.value?.setError(false);
        postalCodeComponent.value?.setError(false);

        const currentCity = city.value;
        const currentPostalCode = postalCode.value;
        const currentStreet = street.value;

        let isDataValid = true;


        if (streetComponent.value != null && !streetComponent.value!.isValid()) {
            isDataValid = false;
            streetComponent.value!.setError(true);
        }

        if (postalCodeComponent.value != null && !postalCodeComponent.value!.isValid()) {
            isDataValid = false;
            postalCodeComponent.value!.setError(true);
        }

        if (cityComponent.value != null && !cityComponent.value!.isValid()) {
            isDataValid = false;
            cityComponent.value!.setError(true);
        }

        if (isDataValid) {
            try {
                isLoading.value = true;

                const orderApplicationId = cart.orderService?.useCase?.id;
                if (orderApplicationId == null) throw new Error("Order application is must be specified");
                if (cart.currentCart == null) throw new Error("Current cart is null");

                let newDeliveryAddress = await customerDao.createOrUpdateDeliveryAddress({
                    orderApplicationId: orderApplicationId,
                    postalCode: currentPostalCode!,
                    city: currentCity!,
                    street: currentStreet!,
                    addressDetails: addressDetails.value,
                    deliveryAddressId: cart.currentCart?.deliveryAddress?.id,
                    cartId: cart.currentCart.cartId,
                    country: country.value,
                });

                cart.setDeliveryAddress(newDeliveryAddress);

                if (props.hide) {
                    props.hide();
                }
            } catch (e) {
                logError(e);

                if (e instanceof DaoError) {
                    if (e.type == exceptionTypes.deliveryAddressNotInScopeOfAnyDeliveryAreaException) {
                        Popup.show({
                            message: translations.deliveryAddress.deliveryAddressNotInScopeError,
                        });
                    } else {
                        Popup.show({
                            message: trans.general.operationFailed,
                        });
                    }
                } else {
                    Popup.show({
                        message: trans.general.operationFailed,
                    });
                }
            } finally {
                isLoading.value = false;
            }
        }
    }
</script>

<style scoped lang="scss">
    .enter-delivery-address-form {
        > .delivery-address-input {
            margin-bottom: 12px;
        }
        > .full-name-container {
            display: flex;
            width: 100%;

            .first-name-input {
                flex-grow: 1;
                margin-right: 12px;
            }

            .last-name-input {
                flex-grow: 1;
                margin-left: 12px;
            }
        }

        > .save-btn {
            height: 46px;
            font-size: 16px;
            font-weight: 300;
            width: 100%;
            border-radius: 23px !important;
            margin-bottom: 14px;
        }
    }
</style>
